import React from 'react';
import { ApptDetailRow, DateRow, TextRow } from 'components';
import { formatSeconds, Images, goalDataTypeEnums } from 'utils';
import moment from 'moment';

export const goalChartHead = (chatValueType, perType) => [
    { name: 'apptId', title: 'Appt  ID', custom: false, noSearch: true },
    { name: 'dateTime', title: 'Session Date/Time', custom: false, noSearch: true },
    { name: 'targetName', title: 'Target', custom: false, noSearch: true },
    { name: 'trials', title: 'Trials', custom: false, noSearch: true },
    { name: 'collected', title: 'Collected by', custom: false, noSearch: true },
    { name: 'average', title: chatValueType === goalDataTypeEnums.RATE ? 'Rate' : 'Average', custom: false, noSearch: true },
];

export const goalChartBody = () => [
    {
        rowText: (item) => <ApptDetailRow name={item?.apptDisplayId ? item.apptDisplayId : '--'} id={item?.apptId} showWidth={'auto'} />,
    },
    { rowText: (item) => <DateRow date={item?.sessionDate} format={'LLLL'} /> },
    { rowText: (item) => <TextRow name={item?.name} /> },
    {
        button: (fn, item) => (
            <button onClick={() => fn('trial')} className="reset-btn link-btn">
                {`${item?.numTrial}/${item?.minTrial}`}
            </button>
        ),
    },
    { rowText: (item) => <TextRow name={item?.collectedBy} /> },
    { rowText: (item) => <div>{renderSessionAverage(item)}</div> },
];

export const renderSessionAverage = (item) => {
    switch (item?.dataType) {
        case goalDataTypeEnums.RATE:
            return <div className="percent-row-box">{`${item?.data?.value}${item?.data?.info}`}</div>;

        case goalDataTypeEnums.PERCENT:
            return <div className="percent-row-box">{`${item?.data?.value?.toFixed(0)}%`}</div>;

        case goalDataTypeEnums.LATENCY:
            return <div className="percent-row-box">{`${formatSeconds(item?.value)}`}</div>;

        case goalDataTypeEnums.FREQUENCY:
            return <div className="rating-row-box">{`${item?.data?.value}`}</div>;

        case goalDataTypeEnums.RATING:
            return <div className="rating-row-box">{`${item?.data?.value?.toFixed(1)} ${item?.data?.info ? item?.data?.info : ''}`}</div>;

        case goalDataTypeEnums.DURATION:
        case goalDataTypeEnums.IRT:
            return <div className="percent-row-box">{`${formatSeconds(item?.value)}`}</div>;

        case goalDataTypeEnums.WHOLE_INTERVAL:
        case goalDataTypeEnums.PARTIAL_INTERVAL:
        case goalDataTypeEnums.MOMENTARY_TIME_SAMPLING:
            return <div className="percent-row-box">{`${item?.data?.value?.toFixed(0)}%`}</div>;

        case goalDataTypeEnums.SCORE:
            return <div className="percent-row-box">{`${item?.value?.toFixed(2)}`}</div>;

        default:
            return item?.value;
    }
};

export const renderChartTextLabels = (dataType) => {
    switch (dataType) {
        case goalDataTypeEnums.PERCENT:
            return 'Percent Correct';
        case goalDataTypeEnums.FREQUENCY:
            return 'Average of Frequency';
        case goalDataTypeEnums.RATING:
            return 'Rating Scale';
        case goalDataTypeEnums.DURATION:
            return 'Average Duration';
        case goalDataTypeEnums.LATENCY:
            return 'Average Latency';
        case goalDataTypeEnums.SCORE:
            return 'Score';
        case goalDataTypeEnums.WHOLE_INTERVAL:
        case goalDataTypeEnums.PARTIAL_INTERVAL:
        case goalDataTypeEnums.MOMENTARY_TIME_SAMPLING:
            return 'Average';

        default:
            return 'Average';
    }
};

export const goalChartDataNotYet = {
    title: 'You don’t have Targets yet',
    subTitle: 'Nothing new for now. Enjoy the silence while it lasts',
    image: Images.emptyFolder,
};

export const goalChartTabEnums = {
    GOAL: 'GOAL',
    TARGET: 'TARGET',
};
export const goalChartTabTypes = [
    { title: 'Goal Overview', tabValue: goalChartTabEnums.GOAL },
    { title: 'Target Details ', tabValue: goalChartTabEnums.TARGET },
];

export const goalChartEnums = {
    BAR: 'BAR',
    LINE: 'LINE',
};

export const goalChartTypes = [
    { title: 'Bar Chart', chartType: goalChartEnums.BAR },
    { title: 'Line Chart', chartType: goalChartEnums.LINE },
];

export function goalLineData(sessions, selectedDates) {
    if (!sessions?.length || !selectedDates?.length) {
        return { categories: [], series: [] };
    }

    if (selectedDates.length === 1) {
        const categories = sessions.map((s) => moment(s.date).format('DD MMM'));

        const data = sessions.map((s) => s.masteredPercent);

        return {
            categories,
            series: [
                {
                    name: 'Mastered',
                    data,
                },
            ],
        };
    }

    const months = selectedDates.map((d) => moment(d).format('YYYY-MM'));

    const sessionMap = sessions.reduce((acc, s) => {
        acc[moment(s.date).format('YYYY-MM')] = s;
        return acc;
    }, {});

    const data = months.map((month) => {
        const s = sessionMap[month];
        if (!s) return null;
        return s.masteredPercent;
    });

    const categories = selectedDates.map((d) => moment(d).format('YYYY-MM-DD'));

    return {
        categories,
        series: [
            {
                name: 'Mastered',
                data,
            },
        ],
    };
}

export function mergeSessionsByMonthMultiple(sessions, selectedDates) {
    if (!sessions?.length || !selectedDates?.length) {
        return { categories: [], series: [] };
    }

    const months = selectedDates.map((d) => moment(d).format('YYYY-MM'));
    const uniqueMonths = [...new Set(months)];

    if (uniqueMonths.length === 1) {
        const allDays = new Set();
        sessions.forEach((t) => {
            t.sessions.forEach((s) => {
                allDays.add(moment(s.sessionDate).format('YYYY-MM-DD'));
            });
        });

        const days = Array.from(allDays).sort();
        const multipleCategories = days.map((d) => moment(d).format('DD MMM'));

        const multipleSeries = sessions.map((t) => {
            const sessionMap = t.sessions.reduce((acc, s) => {
                const dayKey = moment(s.sessionDate).format('YYYY-MM-DD');
                acc[dayKey] = s.data?.value ?? null;
                return acc;
            }, {});

            const data = days.map((day) => sessionMap[day] ?? null);

            return { name: t.name, data };
        });

        return { multipleCategories, multipleSeries };
    }

    // Multiple months → aggregate per month
    const multipleCategories = selectedDates.map((d) => moment(d).format('YYYY-MM-DD'));

    const multipleSeries = sessions.map((t) => {
        const grouped = t.sessions.reduce((acc, s) => {
            const monthKey = moment(s.sessionDate).format('YYYY-MM');
            if (!acc[monthKey]) acc[monthKey] = [];
            acc[monthKey].push(s);
            return acc;
        }, {});

        const merged = Object.entries(grouped).map(([monthKey, sessArr]) => {
            const avgValue = sessArr.reduce((sum, s) => sum + (s.data?.value || 0), 0) / sessArr.length;
            return { month: monthKey, value: parseFloat(avgValue.toFixed(2)) };
        });

        const data = months.map((monthKey) => {
            const found = merged.find((m) => m.month === monthKey);
            return found ? found.value : null;
        });

        return { name: t.name, data };
    });

    return { multipleCategories, multipleSeries };
}

export const eventEnums = {
    CLIENT_EVENTS: 'CLIENT_EVENTS',
    GOAL_EVENTS: 'GOAL_EVENTS',
    TARGET_EVENTS: 'TARGET_EVENTS',
};

export const eventTypeList = [
    {
        title: 'Client Events',
        shortTitle: 'Client',
        type: eventEnums.CLIENT_EVENTS,
        color: '#0C66E4',
        background: '#dae5f5',
        filter: 'brightness(0) saturate(100%) invert(25%) sepia(53%) saturate(5008%) hue-rotate(209deg) brightness(95%) contrast(91%)',
    },
    {
        title: 'Goal Events',
        shortTitle: 'Goal',
        type: eventEnums.GOAL_EVENTS,
        color: '#2A8E6D',
        background: '#ECFDF3',
        filter: 'brightness(0) saturate(100%) invert(45%) sepia(42%) saturate(603%) hue-rotate(109deg) brightness(93%) contrast(90%)',
    },
    {
        title: 'Target Events',
        shortTitle: 'Target',
        type: eventEnums.TARGET_EVENTS,
        color: '#FFC051',
        background: '#FFFAEB',
        filter: 'brightness(0) saturate(100%) invert(66%) sepia(92%) saturate(297%) hue-rotate(352deg) brightness(99%) contrast(103%)',
    },
];

export function chartEventLines(goalEvents, clientEvents, targetEvents, eventTypes) {
    const verticalLines = [];

    if (eventTypes?.CLIENT_EVENTS && clientEvents?.length) {
        verticalLines.push(
            ...clientEvents?.map((i) => ({
                date: moment(i?.eventDate).format('YYYY-MM-DD'),
                color: '#0C66E4',
                background: '#dae5f5',
                label: 'Client Event',
                reason: i?.reason,
                staff: i?.staff?.fullName,
            }))
        );
    }

    if (eventTypes?.GOAL_EVENTS && goalEvents?.length) {
        verticalLines.push(
            ...goalEvents?.map((i) => ({
                date: moment(i?.eventDate).format('YYYY-MM-DD'),
                color: '#2A8E6D',
                background: '#ECFDF3',
                label: 'Goal Event',
                reason: i?.reason,
                staff: i?.staff?.fullName,
            }))
        );
    }

    if (eventTypes?.TARGET_EVENTS && targetEvents?.length) {
        verticalLines.push(
            ...targetEvents?.map((i) => ({
                date: moment(i?.eventDate).format('YYYY-MM-DD'),
                color: '#FFC051',
                background: '#FFFAEB',
                label: 'Target Event',
                reason: i?.reason,
                staff: i?.staff?.fullName,
            }))
        );
    }

    return {
        chartEventData: verticalLines,
    };
}
